import { NotifierTypes } from 'constants/notifierConstants';
import { selectCurrentTeam, selectIsActionsBlocked, showNotifier } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { FC, createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import routesPath from 'routes/RoutesPath';
import { InvoicesRouteState } from 'routes/types';
import { LoadingSpinnerAnimation } from 'assets/animations';
import GenericError from 'components/genric-error/GenericError';
import { useEditInvoiceMutation, useLazyGetInvoiceByIdQuery } from '../api';
import { PreviewBar } from '../components';
import { InvoiceTabEnum, minimumLineItemsError } from '../create-invoice/constants';
import { EditInvoicePayload, IInvoiceAttachmentRequest, TaxesAndFeesResponse } from '../invoices.model';
import InvoiceOptionsSectionEdit from './components/InvoiceOptionsSectionEdit';
import InvoicesTabSideNav from './components/InvoicesTabSideNavEdit';
import LineItemsSectionEdit from './components/LineItemsSectionEdit';
import NotesAttachmentsSectionEdit from './components/NotesAttachmentsSectionEdit';
import PaymentOptionsSectionEdit from './components/PaymentOptionsSectionEdit';
import { constructArrayObjectAsString, throttle } from 'utils/commonUtils';
import {
  addInvoiceValidationErrorEditPage,
  enableCustomerNotSelectedErrorEditPage,
  initialiseInvoicePaymentModesEdit,
  resetInvoiceEditSlice,
  selectEstimateNumberForInvoiceEditPage,
  selectEstimateStatusForInvoiceEditPage,
  selectInvoiceEditPage,
  selectInvoiceSliceInitializationInProgress,
  selectIsBackFromCreateTaxInEditSlice,
  selectIsFromPreview,
  selectIsInvoiceFromJobEditPage,
  selectIsSignatureEnabledEditPage,
  selectJobDataForInvoiceEditPage,
  selectJobNumberForInvoiceEditPage,
  toggleIsFromPreview,
  updateInvoiceEditedResponse,
  updateInvoiceInitializationInProgress,
  updateIsSignatureEnabledEditPage,
  updateSelectedTNC
} from './invoicesEditSlice';
import { ErrorResponse } from 'containers/app/app.model';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsInvoices } from '../events';
import {
  checkIfAnyPaymentModeIsEligible,
  getLineItemsArrayForEvent,
  getServiceChargeEventPayload,
  hasChangesToSync,
  prepareDiscountRelatedPayload,
  prepareLineItemList,
  prepareServiceChargeListRequestPayload,
  prepareSummatedUniqueTaxList
} from '../utils';
import { InvoiceSourceEnum, InvoiceVersion } from '../constants';
import { DispatchDestination, invoiceMinimumAmount } from 'constants/common';
import { useGetItemSuggestionsAndGlobalTaxesAndFeesQuery } from 'containers/product-catalog/api';
import SignatureOptionsSection from '../create-invoice/components/SignatureOptionsSection';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { FeatureToggleState } from 'containers/settings/constants';
import EditInvoiceCustomer from './components/EditInvoiceCustomer';
import { CrossIconvector } from 'assets/icons';
import { GenericModal } from 'components';
import NoSelfCheckoutPopup from '../components/NoSelfCheckoutPopup';
import { InvoiceErrorFieldsDefault } from '../create-invoice/types';
import InvoiceErrorsPopup from '../components/InvoiceErrorsPopup';
import { updateCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { EstimateStatus } from 'containers/estimates/constants';
import BlockedInfoPopup from 'containers/home/components/BlockedInfoPopup';

interface IEditInvoiceContext {
  selectedInvoiceTab: InvoiceTabEnum;
  setSelectedInvoiceTab: React.Dispatch<React.SetStateAction<InvoiceTabEnum>>;
}

export const EditInvoiceContext = createContext<IEditInvoiceContext>({
  selectedInvoiceTab: InvoiceTabEnum.BILL_TO,
  setSelectedInvoiceTab: null
});

interface EditInvoiceProps {
  goToPreviewPage: () => void;
  taxesAndFeesData: TaxesAndFeesResponse;
  isLoadingTaxesAndFees: boolean;
}

const EditInvoice: FC<EditInvoiceProps> = ({ goToPreviewPage, taxesAndFeesData, isLoadingTaxesAndFees }) => {
  const invoiceInitInProgress = useAppSelector(selectInvoiceSliceInitializationInProgress);
  const isBackFromCreateTaxRoute = useAppSelector(selectIsBackFromCreateTaxInEditSlice);
  const selectedIsSignatureEnabled = useAppSelector(selectIsSignatureEnabledEditPage);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const selectedInvoice = useAppSelector(selectInvoiceEditPage);
  const isFromPreview = useAppSelector(selectIsFromPreview);
  const isInvoiceFromJob = useAppSelector(selectIsInvoiceFromJobEditPage);
  const jobData = useAppSelector(selectJobDataForInvoiceEditPage);
  const invoiceFromJobNumber = useAppSelector(selectJobNumberForInvoiceEditPage);
  const estimateNumber = useAppSelector(selectEstimateNumberForInvoiceEditPage);
  const estimateStatus = useAppSelector(selectEstimateStatusForInvoiceEditPage);
  const isEstimatePending = estimateStatus?.toUpperCase() === EstimateStatus.PENDING;

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showNoSelfCheckoutPopup, setShowNoSelfCheckoutPopup] = useState(false);
  const [showInvoiceErrorsPopup, setShowInvoiceErrorsPopup] = useState({
    open: false,
    errors: InvoiceErrorFieldsDefault
  });
  const [showMerchantBlockedPopup, setShowMerchantBlockedPopup] = useState(false);

  const [getInvoiceQuery, getInvoiceQueryState] = useLazyGetInvoiceByIdQuery();

  useGetItemSuggestionsAndGlobalTaxesAndFeesQuery({
    teamId: currentTeam.id,
    profession: currentTeam.profession,
    dispatchDestination: DispatchDestination.EDIT_INVOICE_SLICE
  });

  const { data: merchantCustomisations, isSuccess: merchantCustomisationsSuccess } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });

  useEffect(() => {
    if (merchantCustomisationsSuccess && selectedInvoice?.finalAmountEditPage) {
      dispatch(updateCustomizations(merchantCustomisations));
      if (!invoiceInitInProgress) {
        if (merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON) {
          dispatch(updateIsSignatureEnabledEditPage(true));
        }
        dispatch(
          initialiseInvoicePaymentModesEdit({
            paymentModes: merchantCustomisations.paymentModes,
            finalAmount: selectedInvoice.finalAmountEditPage
          })
        );
      }
    }
  }, [merchantCustomisationsSuccess, invoiceInitInProgress]);

  const [
    editInvoiceMutation,
    {
      isSuccess: isEditInvoiceSuccess,
      data: invoiceEditedResponse,
      isError: isInvoiceEditError,
      error: invoiceEditError,
      isLoading: isEditInvoiceLoading
    }
  ] = useEditInvoiceMutation();
  const navigate = useNavigate();
  const { invoiceId } = useParams();

  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(InvoiceTabEnum.BILL_TO);

  const { data: getInvoiceResponse, isSuccess: getInvoiceSuccess, isError: isGetInvoiceErr } = getInvoiceQueryState;
  useEffect(() => {
    if (!isFromPreview && !isBackFromCreateTaxRoute && !isLoadingTaxesAndFees) {
      dispatch(updateInvoiceInitializationInProgress(true));
      getInvoiceQuery({ invoiceId: invoiceId as string, isFromEdit: true, teamId: currentTeam.id });
    } else {
      setIsLoading(false);
      dispatch(toggleIsFromPreview(false));
    }
  }, [invoiceId, isLoadingTaxesAndFees]);

  useEffect(() => {
    if (getInvoiceResponse) {
      setIsLoading(false);
    }
  }, [getInvoiceResponse]);

  useEffect(() => {
    if (isGetInvoiceErr) {
      dispatch(updateInvoiceInitializationInProgress(false));
      setIsLoading(false);
    }
  }, [isGetInvoiceErr]);

  useEffect(() => {
    if (getInvoiceSuccess && getInvoiceResponse && !getInvoiceResponse?.isModifiable) {
      navigate(-1);
    }
    if (getInvoiceSuccess && getInvoiceResponse) {
      dispatch(updateSelectedTNC(getInvoiceResponse.tnc));
    }
  }, [getInvoiceResponse, getInvoiceSuccess]);

  useEffect(() => {
    if (isEditInvoiceSuccess) {
      dispatch(resetInvoiceEditSlice());

      dispatch(updateInvoiceEditedResponse(invoiceEditedResponse));
      const invoiceRouteState: InvoicesRouteState = {
        showShareModal: true,
        isEditInvoiceShare: true,
        showSidePanel: false
      };
      navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
    }
  }, [isEditInvoiceSuccess]);

  const handleScrollToTab = (invoiceTab: InvoiceTabEnum) => {
    const tabElem = document.getElementById(invoiceTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  };

  useEffect(() => {
    handleScrollToTab(selectedInvoiceTab);
  }, [selectedInvoiceTab]);

  useEffect(() => {
    if (isInvoiceEditError) {
      const error = invoiceEditError as ErrorResponse;

      if (error?.data?.details) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: error.data.details,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Something went wrong',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      }
    }
  }, [isInvoiceEditError]);

  const getAttachmentArray = () => {
    const attachmentArray: IInvoiceAttachmentRequest[] = selectedInvoice.selectedInvoiceAttachmentsEditPage?.map(
      attachment => {
        return {
          title: attachment.title,
          url: attachment.url,
          description: attachment.description,
          source: attachment.source
        };
      }
    );
    return attachmentArray;
  };

  const hasDataChangesInInvoice = () => {
    return hasChangesToSync(
      selectedInvoice.invoiceCustomerEditPage,
      selectedInvoice.initialInvoiceCustomer,
      selectedInvoice.invoiceLineItemsEditPage,
      selectedInvoice.initialInvoiceLineItemsEditPage,
      selectedInvoice.selectedServiceCharge,
      selectedInvoice.initialServiceCharge,
      selectedInvoice.invoiceSubTotalEditPage,
      selectedInvoice.initialSubTotalEditPage,
      selectedInvoice.isServiceFeeEnabledEditPage,
      selectedInvoice.isServiceFeeEnabledEditPageInitial,
      selectedInvoice.currentSurchargeEditPage,
      selectedInvoice.initialSurchargeStateEditPage
    );
  };

  const onEditAndShare = () => {
    if (isActionsBlocked) {
      setShowMerchantBlockedPopup(true);
      return;
    }
    let isPageIncomplete = false;
    let errors = InvoiceErrorFieldsDefault;
    if (selectedInvoice.invoiceLineItemsEditPage.length === 0) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }

    if (selectedInvoice.invoiceCustomerEditPage === null) {
      isPageIncomplete = true;
      errors = { ...errors, customer: true };
    }
    const invoiceMinAmount = +merchantCustomisations?.minimumAmount || invoiceMinimumAmount;
    if (selectedInvoice.invoiceSubTotalEditPage < invoiceMinAmount) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }

    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModesEdit.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      const isAnyPaymentModeEligible = checkIfAnyPaymentModeIsEligible(
        merchantCustomisations.paymentModes,
        selectedInvoice.finalAmountEditPage
      );
      if (!isAnyPaymentModeEligible) {
        isPageIncomplete = true;
        errors = { ...errors, paymentMethods: true };
      }
    }

    if (isPageIncomplete) {
      setShowInvoiceErrorsPopup({ open: true, errors });
      return;
    }

    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }

    if (
      ((isInvoiceFromJob && jobData[0]?.isEditable) || (estimateNumber && isEstimatePending)) &&
      hasDataChangesInInvoice()
    ) {
      setShowSyncConfirmationPopup(true);
    } else {
      handleEditInvoice();
    }
  };

  const getPaymentModes = () => {
    const selectedModes = selectedInvoice.invoicePaymentModesEdit.filter(item => item.isSelected).map(item => item.id);

    const updatedPaymentModes = merchantCustomisations.paymentModes.map(paymentMode => {
      if (paymentMode.teamLevelVisiblity) {
        if (selectedModes.includes(paymentMode.id)) {
          return {
            id: paymentMode.id,
            enabled: true
          };
        }
        return {
          id: paymentMode.id,
          enabled: false
        };
      }
      return {
        id: paymentMode.id,
        enabled: paymentMode.enabled
      };
    });
    return updatedPaymentModes;
  };

  const handleEditInvoice = () => {
    const serviceChargeListPayload = prepareServiceChargeListRequestPayload(selectedInvoice.selectedServiceCharge);

    const editInvoicePayload: EditInvoicePayload = {
      invoiceId: invoiceId,
      invoicePayload: {
        version: InvoiceVersion.THREE_POINT_ZERO,
        teamId: currentTeam.id.toString(),
        storeName: currentTeam.storeName,
        amount: selectedInvoice.invoiceSubTotalEditPage.toString(),
        comment: selectedInvoice.commentEditPage,
        customerId: selectedInvoice?.invoiceCustomerEditPage?.ID,
        lineItems: prepareLineItemList(selectedInvoice.invoiceLineItemsEditPage),
        ...prepareDiscountRelatedPayload(selectedInvoice.discountFormDataEditPage),
        surchargePercentage: selectedInvoice.isServiceFeeEnabledEditPage
          ? selectedInvoice.currentSurchargeEditPage.percentageWithBps ?? '0'
          : '0',
        surchargeAmount: selectedInvoice.isServiceFeeEnabledEditPage
          ? selectedInvoice.currentSurchargeEditPage.valueInCurrencyWithBps?.toString() ?? '0'
          : '',
        isServiceFeeEnabled: selectedInvoice.isServiceFeeEnabledEditPage,
        attachments: getAttachmentArray(),
        tipEnabled: selectedInvoice.tipEnabledEditPage,
        tncId: getInvoiceResponse?.tnc?.tncId,
        ...prepareSummatedUniqueTaxList({
          appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
          invoiceLineItems: selectedInvoice.invoiceLineItemsEditPage,
          selectedServiceCharge: selectedInvoice.selectedServiceCharge
        }),
        serviceCharges: serviceChargeListPayload,
        serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : '0',
        source: InvoiceSourceEnum.WEB,
        isSignatureEnabled: selectedIsSignatureEnabled,
        serviceAddressId: selectedInvoice?.invoiceCustomerEditPage?.serviceAddress?.[0]?.id ?? null,
        paymentModes: getPaymentModes()
      }
    };

    const serviceChargeEventPayload = getServiceChargeEventPayload(serviceChargeListPayload);
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesEditInvoiceSave, {
      customerName: selectedInvoice?.invoiceCustomerEditPage?.name,
      customerId: selectedInvoice?.invoiceCustomerEditPage?.ID,
      teamId: currentTeam.id.toString(),
      storeName: currentTeam.storeName,
      amount: selectedInvoice.invoiceSubTotalEditPage.toString(),
      'Line Items': constructArrayObjectAsString(
        getLineItemsArrayForEvent(selectedInvoice.invoiceLineItemsEditPage),
        'Line item'
      ),
      ...prepareDiscountRelatedPayload(selectedInvoice.discountFormDataEditPage),
      surchargePercentage: selectedInvoice.isServiceFeeEnabledEditPage
        ? selectedInvoice.currentSurchargeEditPage.percentageWithBps ?? '0'
        : '0',
      ...(serviceChargeEventPayload?.length > 0 ? { serviceCharges: serviceChargeEventPayload } : {}),
      serviceAddressId: selectedInvoice?.invoiceCustomerEditPage?.serviceAddress?.[0]?.id ?? ''
    });
    editInvoiceMutation(editInvoicePayload);
  };

  const handleOnCreateInvoiceWithoutSelfCheckout = () => {
    if ((isInvoiceFromJob || (estimateNumber && isEstimatePending)) && hasDataChangesInInvoice()) {
      setShowSyncConfirmationPopup(true);
    } else {
      handleEditInvoice();
    }
  };

  const handleOnEditPaymentMethods = () => {
    handleCloseNoSelfCheckoutPopup();
    handleScrollToTab(InvoiceTabEnum.PAYMENT_OPTIONS);
  };

  const handleCloseNoSelfCheckoutPopup = () => {
    setShowNoSelfCheckoutPopup(false);
  };

  const handleCloseInvoiceErrorsPopup = () => {
    setShowInvoiceErrorsPopup({ open: false, errors: InvoiceErrorFieldsDefault });
  };

  const editAndShareHandler = useCallback(throttle(onEditAndShare, 800), [
    selectedInvoice,
    isInvoiceFromJob,
    estimateNumber,
    isActionsBlocked
  ]);

  const onBackIconClick = () => {
    dispatch(resetInvoiceEditSlice());
    navigate(-1);
  };

  const handleSignatureStateChange = (value: boolean) => {
    dispatch(updateIsSignatureEnabledEditPage(value));
  };

  const checkIsSignatureDisabled = () => {
    if (merchantCustomisationsSuccess) {
      return merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON;
    }
    return false;
  };

  const getSyncPopupTitleAndSubtitle = (type: 'title' | 'subtitle') => {
    const title = `Save and sync changes to the related ${isInvoiceFromJob || invoiceFromJobNumber ? 'job' : ''} ${
      (isInvoiceFromJob || invoiceFromJobNumber) && estimateNumber && isEstimatePending ? 'and' : ''
    } ${estimateNumber && isEstimatePending ? 'estimate' : ''}?`;
    const subtitle = `Changes in your invoices will be applied to the associated ${
      isInvoiceFromJob || invoiceFromJobNumber ? `job ${invoiceFromJobNumber}` : ''
    } ${(isInvoiceFromJob || invoiceFromJobNumber) && estimateNumber && isEstimatePending ? 'and' : ''} ${
      estimateNumber && isEstimatePending ? `estimate ${estimateNumber}` : ''
    } as well.`;
    return type === 'title' ? title : subtitle;
  };

  const onPreviewButtonClick = () => {
    let isPageIncomplete = false;
    if (selectedInvoice.invoiceLineItemsEditPage.length === 0) {
      isPageIncomplete = true;
      dispatch(addInvoiceValidationErrorEditPage(minimumLineItemsError));
    }

    if (selectedInvoice.invoiceCustomerEditPage === null) {
      isPageIncomplete = true;
      dispatch(enableCustomerNotSelectedErrorEditPage());
    }

    if (isPageIncomplete) {
      return;
    }
    goToPreviewPage();
  };

  if (isLoading || invoiceInitInProgress)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
      </div>
    );

  if (!isLoading && isGetInvoiceErr)
    return (
      <div className="flex h-full w-full justify-center">
        <GenericError handleErrorBack={() => navigate(-1)} />
      </div>
    );

  return (
    <EditInvoiceContext.Provider value={{ selectedInvoiceTab, setSelectedInvoiceTab }}>
      <div className="flex h-full w-full flex-col border">
        <PreviewBar
          className="border border-borderGray"
          title="Edit Invoice"
          onPrimaryClick={editAndShareHandler}
          onReturn={onBackIconClick}
          primaryBtnTitle="Save & Share"
          secondaryBtnTitle="Preview"
          disablePrimaryBtn={false}
          onSecondaryClick={onPreviewButtonClick}
          primaryBtnId="save-and-share-invoice-button"
          secondaryBtnId="edit-invoice-preview-button"
        />
        <div className="flex h-full w-full justify-center overflow-hidden bg-primary">
          <div
            className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-12
          pr-[50px] lg:flex lg:w-[24rem] 1439px:w-[31.25rem]">
            <InvoicesTabSideNav context={EditInvoiceContext} />
          </div>

          <div
            id="scrollable div"
            className="customNormalScroll flex grow flex-col space-y-[30px]
           overflow-y-auto p-12 pl-[50px] 1439px:space-y-11">
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.BILL_TO}>
              <EditInvoiceCustomer context={EditInvoiceContext} />
            </div>

            {/* TODO invoice options in edit page */}
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.INVOICE_OPTIONS}>
              <InvoiceOptionsSectionEdit context={EditInvoiceContext} />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.LINE_ITEMS}>
              <LineItemsSectionEdit
                taxesAndFees={taxesAndFeesData}
                isLoadingTaxesAndFees={isLoadingTaxesAndFees}
                context={EditInvoiceContext}
              />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.NOTES_ATTACHMENTS}>
              <NotesAttachmentsSectionEdit context={EditInvoiceContext} isLoadingTaxesAndFees={isLoadingTaxesAndFees} />
            </div>

            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.PAYMENT_OPTIONS}>
              <PaymentOptionsSectionEdit isInvoiceEdit={true} context={EditInvoiceContext} />
            </div>
            <div className="lg:w-4xl flex max-w-4xl flex-col" id={InvoiceTabEnum.SIGNATURES}>
              <SignatureOptionsSection
                signatureState={selectedIsSignatureEnabled}
                updateSignatureState={handleSignatureStateChange}
                context={EditInvoiceContext}
                isDisabled={checkIsSignatureDisabled()}
                id="edit-invoice-allow-sign-toggle-switch"
              />
            </div>
          </div>
        </div>
      </div>
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title={getSyncPopupTitleAndSubtitle('title')}
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={getSyncPopupTitleAndSubtitle('subtitle')}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleEditInvoice}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => <CrossIconvector height={18} width={18} title="sync-popup-close" />}
          isActionInProgress={isEditInvoiceLoading}
          height="h-[262px]"
          className="!px-5 !py-6"
        />
      )}
      {showNoSelfCheckoutPopup && (
        <NoSelfCheckoutPopup
          isEdit={true}
          onContinue={handleOnCreateInvoiceWithoutSelfCheckout}
          onEditPaymentMethods={handleOnEditPaymentMethods}
          onClose={handleCloseNoSelfCheckoutPopup}
          isActionLoading={isEditInvoiceLoading}
        />
      )}
      {showInvoiceErrorsPopup.open && (
        <InvoiceErrorsPopup
          onClose={handleCloseInvoiceErrorsPopup}
          errorFields={showInvoiceErrorsPopup.errors}
          maximumAmount={+merchantCustomisations?.maximumAmount || 0}
          minimumAmount={+merchantCustomisations?.minimumAmount || 0}
          onContinue={handleCloseInvoiceErrorsPopup}
        />
      )}
      {showMerchantBlockedPopup && (
        <BlockedInfoPopup isOpen={showMerchantBlockedPopup} onClose={() => setShowMerchantBlockedPopup(false)} />
      )}
    </EditInvoiceContext.Provider>
  );
};

export default EditInvoice;
